import type { FocusEvent, MouseEvent, PointerEvent } from 'react'
import type { ItemTip, PortfolioBar, PortfolioDayMarker } from './layout'

/** Anything on a row that opens a sheet: a stay, block or gap bar, a marker or "+N". */
export type PortfolioItem = PortfolioBar | PortfolioDayMarker

/** Where a tooltip goes: its top-left corner, or its bottom edge at `above` when it does not fit below. */
export interface TipAnchor {
  x: number
  y: number
  above: number
}

export interface TipState {
  tip: ItemTip
  anchor: TipAnchor
}

/**
 * The grid's one tooltip, held outside React state: hovering an item shows it
 * and must not re-render a row, let alone the grid. `PortfolioTooltip` is the
 * only subscriber.
 */
export interface TipStore {
  get: () => TipState | null
  subscribe: (listener: () => void) => () => void
  show: (tip: ItemTip, anchor: TipAnchor) => void
  hide: () => void
}

export function createTipStore(): TipStore {
  let state: TipState | null = null
  const listeners = new Set<() => void>()
  const set = (next: TipState | null) => {
    if (next === state) return
    state = next
    listeners.forEach(listener => listener())
  }
  return {
    get: () => state,
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    show: (tip, anchor) => set({ tip, anchor }),
    hide: () => set(null),
  }
}

/**
 * What the grid does when an item or a day is used. One stable object for the
 * whole grid, so the memoised rows never re-render because of it.
 */
export interface PortfolioItemActions {
  /** Opens the item's sheet; `opener` gets focus back when the sheet closes. */
  open: (propertyId: string, item: PortfolioItem, opener: HTMLElement) => void
  /** Opens the property's day list for the day at `axisIndex`; `opener` gets focus back. */
  openDay: (propertyId: string, axisIndex: number, opener: HTMLElement) => void
  /**
   * Opens "Block dates" for the property on a dragged range (CAP-1419): `first`
   * and `last` are day numbers, both nights blocked; `opener` gets focus back.
   */
  openNewBlock: (propertyId: string, first: number, last: number, opener: HTMLElement) => void
  tips: TipStore
}

/** Marks an item, so a gesture on the row's track can tell an item from an empty day. */
export const ITEM_ATTRIBUTE = 'data-portfolio-item'

/** Marks what sits on a track but is not a day: the loading skeleton, the error and caveat chips. */
export const INERT_ATTRIBUTE = 'data-portfolio-inert'

/** Pointer below and right of the cursor, as the approved mock places it. */
const POINTER_OFFSET_X = 14
const POINTER_OFFSET_Y = 18
const FOCUS_GAP = 6

/**
 * Keyboard focus, not a click or a tap: a tooltip follows the keyboard, never a
 * touch (the sheet is the touch path). Where the browser cannot say, it shows.
 */
export function isFocusVisible(element: HTMLElement): boolean {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * The handlers every item on a row wears: open its sheet on click (and Enter
 * or Space, as a button), and show its tooltip on hover and keyboard focus.
 * React delegates them from its root, so they add no listener per item.
 */
export function itemHandlers(
  actions: PortfolioItemActions,
  propertyId: string,
  item: PortfolioItem
) {
  return {
    [ITEM_ATTRIBUTE]: '',
    onClick: (event: MouseEvent<HTMLElement>) => {
      // The row's track treats a click on an empty day as "open the day".
      event.stopPropagation()
      actions.tips.hide()
      actions.open(propertyId, item, event.currentTarget)
    },
    onPointerEnter: (event: PointerEvent<HTMLElement>) => {
      // Not for a touch, and not under a drag passing over the item (CAP-1419).
      if (event.pointerType === 'touch' || event.buttons !== 0) return
      actions.tips.show(item.tip, {
        x: event.clientX + POINTER_OFFSET_X,
        y: event.clientY + POINTER_OFFSET_Y,
        above: event.clientY - FOCUS_GAP,
      })
    },
    onPointerLeave: () => actions.tips.hide(),
    onFocus: (event: FocusEvent<HTMLElement>) => {
      const element = event.currentTarget
      if (!isFocusVisible(element)) return
      const rect = element.getBoundingClientRect()
      actions.tips.show(item.tip, {
        x: rect.left,
        y: rect.bottom + FOCUS_GAP,
        above: rect.top - FOCUS_GAP,
      })
    },
    onBlur: () => actions.tips.hide(),
  }
}
